// Copyright (C) 2020-2022 Intel Corporation
// Copyright (C) 2022-2024 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React from 'react';
import ReactDOM from 'react-dom';
import { withRouter, RouteComponentProps } from 'react-router';

import Modal from 'antd/lib/modal';
import Text from 'antd/lib/typography/Text';
import InputNumber from 'antd/lib/input-number';
import Checkbox from 'antd/lib/checkbox';
import Collapse from 'antd/lib/collapse';

import CVATTooltip from 'components/common/cvat-tooltip';
import Menu, { MenuInfo } from 'components/dropdown-menu';

import { getCore, JobStage } from 'cvat-core-wrapper';
import AnnotationsActionsModalContent from '../annotations-actions/annotations-actions-modal';

const core = getCore();

interface Props {
    taskMode: string;
    jobInstance: any;
    onClickMenu(params: MenuInfo): void;
    stopFrame: number;
    removeAnnotations(startnumber: number | null, endnumber: number | null, delTrackKeyframesOnly: boolean): void;
    setForceExitAnnotationFlag(forceExit: boolean): void;
    saveAnnotations(afterSave?: () => void): void;
}

export enum Actions {
    LOAD_JOB_ANNO = 'load_job_anno',
    LOAD_JOB_REFERENCE = 'load_job_reference',
    EXPORT_JOB_DATASET = 'export_job_dataset',
    EXPORT_JOB_BLENDER = 'export_job_blender',
    REMOVE_ANNOTATIONS = 'remove_annotations',
    RUN_ACTIONS = 'run_actions',
    OPEN_REFERENCE = 'open_reference',
    FINISH_JOB = 'finish_job',
    RENEW_JOB = 'renew_job',
}

function AnnotationMenuComponent(props: Props & RouteComponentProps): JSX.Element {
    const {
        jobInstance,
        stopFrame,
        history,
        onClickMenu,
        removeAnnotations,
        setForceExitAnnotationFlag,
        saveAnnotations,
    } = props;

    const jobStage = jobInstance.stage;
    const jobState = jobInstance.state;
    const referenceID = jobInstance.referenceId;
    const { ReferenceJobState } = core.enums;

    function onClickMenuWrapper(params: MenuInfo): void {
        function checkUnsavedChanges(_params: MenuInfo): void {
            if (jobInstance.annotations.hasUnsavedChanges()) {
                Modal.confirm({
                    title: 'The job has unsaved annotations',
                    content: 'Would you like to save changes before continue?',
                    className: 'cvat-modal-content-save-job',
                    okButtonProps: {
                        children: 'Save',
                    },
                    cancelButtonProps: {
                        children: 'No',
                    },
                    onOk: () => {
                        saveAnnotations(() => onClickMenu(_params));
                    },
                    onCancel: () => {
                        // do not ask leave confirmation
                        setForceExitAnnotationFlag(true);
                        setTimeout(() => {
                            onClickMenu(_params);
                        });
                    },
                });
            } else {
                onClickMenu(_params);
            }
        }

        if (params.key === Actions.REMOVE_ANNOTATIONS) {
            let removeFrom: number | null;
            let removeUpTo: number | null;
            let removeOnlyKeyframes = false;
            const { Panel } = Collapse;
            Modal.confirm({
                title: 'Remove Annotations',
                content: (
                    <div>
                        <Text>You are going to remove the annotations from the client. </Text>
                        <Text>It will stay on the server till you save the job. Continue?</Text>
                        <br />
                        <br />
                        <Collapse bordered={false}>
                            <Panel header={<Text>Select Range</Text>} key={1}>
                                <Text>From: </Text>
                                <InputNumber
                                    min={0}
                                    max={stopFrame}
                                    onChange={(value) => {
                                        removeFrom = value;
                                    }}
                                />
                                <Text> To: </Text>
                                <InputNumber
                                    min={0}
                                    max={stopFrame}
                                    onChange={(value) => {
                                        removeUpTo = value;
                                    }}
                                />
                                <CVATTooltip title='Applicable only for annotations in range'>
                                    <br />
                                    <br />
                                    <Checkbox
                                        onChange={(check) => {
                                            removeOnlyKeyframes = check.target.checked;
                                        }}
                                    >
                                        Delete only keyframes for tracks
                                    </Checkbox>
                                </CVATTooltip>
                            </Panel>
                        </Collapse>
                    </div>
                ),
                className: 'cvat-modal-confirm-remove-annotation',
                onOk: () => {
                    removeAnnotations(removeFrom, removeUpTo, removeOnlyKeyframes);
                },
                okButtonProps: {
                    type: 'primary',
                    danger: true,
                },
                okText: 'Delete',
            });
        } else if (params.key.startsWith('state:')) {
            Modal.confirm({
                title: 'Do you want to change current job state?',
                content: `Job state will be switched to "${params.key.split(':')[1]}". Continue?`,
                okText: 'Continue',
                cancelText: 'Cancel',
                className: 'cvat-modal-content-change-job-state',
                onOk: () => {
                    checkUnsavedChanges(params);
                },
            });
        } else if (params.key === Actions.FINISH_JOB) {
            Modal.confirm({
                title: 'The job stage is going to be switched',
                content: 'Stage will be changed to "acceptance". Would you like to continue?',
                okText: 'Continue',
                cancelText: 'Cancel',
                className: 'cvat-modal-content-finish-job',
                onOk: () => {
                    checkUnsavedChanges(params);
                },
            });
        } else if (params.key === Actions.RENEW_JOB) {
            Modal.confirm({
                title: 'Do you want to renew the job?',
                content:
                    'Stage will be set to "in progress", state will be set to "annotation". Would you like to continue?',
                okText: 'Continue',
                cancelText: 'Cancel',
                className: 'cvat-modal-content-renew-job',
                onOk: () => {
                    onClickMenu(params);
                },
            });
        } else {
            onClickMenu(params);
        }
    }

    const computeClassName = (menuItemState: string): string => {
        if (menuItemState === jobState) return 'cvat-submenu-current-job-state-item';
        return '';
    };

    return (
        <Menu
            triggerSubMenuAction='click'
            onClick={(params: MenuInfo) => {
                onClickMenuWrapper(params);
            }}
            className='cvat-annotation-menu'
            selectable={false}
        >
            <Menu.Item key={Actions.LOAD_JOB_ANNO}>Upload annotations</Menu.Item>
            <Menu.Item key={Actions.EXPORT_JOB_DATASET}>Export job dataset</Menu.Item>
            <Menu.Item key={Actions.REMOVE_ANNOTATIONS}>Remove annotations</Menu.Item>
            <Menu.Item
                key={Actions.RUN_ACTIONS}
                onClick={() => {
                    const div = window.document.createElement('div');
                    window.document.body.append(div);
                    ReactDOM.render(<AnnotationsActionsModalContent onClose={() => div.remove()} />, div);
                }}
            >
                Run actions
            </Menu.Item>
            <Menu.Item key={Actions.OPEN_REFERENCE}>
                <a
                    href={`/references/${referenceID}`}
                    onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        history.push(`/references/${referenceID}`);
                        return false;
                    }}
                >
                    Open the reference
                </a>
            </Menu.Item>
            <Menu.SubMenu
                popupClassName='cvat-annotation-menu-job-state-submenu'
                key='job-state-submenu'
                title='Change job state'
            >
                <Menu.Item key={`state:${ReferenceJobState.NEW}`}>
                    <Text className={computeClassName(ReferenceJobState.NEW)}>{ReferenceJobState.NEW}</Text>
                </Menu.Item>
                <Menu.Item key={`state:${ReferenceJobState.IN_PROGRESS}`}>
                    <Text className={computeClassName(ReferenceJobState.IN_PROGRESS)}>
                        {ReferenceJobState.IN_PROGRESS}
                    </Text>
                </Menu.Item>
                <Menu.Item key={`state:${ReferenceJobState.REJECTED}`}>
                    <Text className={computeClassName(ReferenceJobState.REJECTED)}>{ReferenceJobState.REJECTED}</Text>
                </Menu.Item>
                <Menu.Item key={`state:${ReferenceJobState.COMPLETED}`}>
                    <Text className={computeClassName(ReferenceJobState.COMPLETED)}>{ReferenceJobState.COMPLETED}</Text>
                </Menu.Item>
            </Menu.SubMenu>
            {[JobStage.ANNOTATION, JobStage.VALIDATION].includes(jobStage) ? (
                <Menu.Item key={Actions.FINISH_JOB}>Finish the job</Menu.Item>
            ) : null}
            {jobStage === JobStage.ACCEPTANCE ? <Menu.Item key={Actions.RENEW_JOB}>Renew the job</Menu.Item> : null}
        </Menu>
    );
}

export default withRouter(AnnotationMenuComponent);
